import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import ContentBox from '../components/ContentBox';
import HabboImage from '../components/HabboImage';
import ArticleNote from '../components/ArticleNote';
import ArticleComments from '../components/ArticleComments';
import LatestArticles from '../components/LatestArticles';
import StaffArticleActions from '../components/StaffArticleActions';
import PollBox from "../components/PollBox";
import ProfileSearch from "../components/ProfileSearch";
import { fetchProfileColor, getAvatarBgStyle } from "../services/profileColorService";

function Article({ userData }) {
    const { dateSlug, titleSlug } = useParams();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [authorBg, setAuthorBg] = useState('#5A2929');

    useEffect(() => {
        loadArticle();
    }, [dateSlug, titleSlug]);

    useEffect(() => {
        if (!article?.author) return;
        let cancelled = false;
        fetchProfileColor(article.author).then((color) => {
            if (!cancelled) setAuthorBg(color);
        });
        return () => { cancelled = true; };
    }, [article?.author]);

    const loadArticle = async () => {
        try {
            setLoading(true);
            setError(false);
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/';
            const response = await fetch(`${apiUrl}articles/${dateSlug}/${titleSlug}`);
            const data = await response.json();

            if (data.success) {
                setArticle(data.article);
            } else {
                setError(true);
                toast.error('Article not found');
            }
        } catch (err) {
            console.error('Error loading article:', err);
            setError(true);
            toast.error('Failed to load article');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getPageTitle = (url) => {
        if (!url) return null;
        try {
            const urlObj = new URL(url);
            return urlObj.hostname.replace('www.', '');
        } catch (e) {
            return url;
        }
    };

    if (loading) {
        return (
            <div className="w-full min-h-screen p-4 lg:p-6 overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
                    <div className="lg:col-span-8">
                        <ContentBox theme="default" tint="maroon">
                            <p className="text-gray-700">Loading article...</p>
                        </ContentBox>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !article) {
        return (
            <div className="w-full min-h-screen p-4 lg:p-6 overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
                    <div className="lg:col-span-8">
                        <ContentBox theme="default" tint="maroon">
                            <p className="text-gray-700">Article not found</p>
                        </ContentBox>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full min-h-screen p-4 lg:p-6 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
                <div className="lg:col-span-8 min-w-0">
                    <ContentBox theme="default" tint="maroon">
                        <div className="space-y-4">
                            <div className="text-sm text-gray-500 tracking-wide flex items-start gap-3">
                                <Link
                                    to={`/members/profile/${encodeURIComponent(article.author)}`}
                                    className="w-[50px] h-[50px] overflow-hidden inline-block relative rounded-full border-[2px] border-coal/20 flex-shrink-0 transition-colors duration-300"
                                    style={getAvatarBgStyle(authorBg)}
                                >
                                    <HabboImage
                                        habbo={article.author}
                                        direction={2}
                                        head_direction={3}
                                        gesture="sml"
                                        className="absolute top-[-16px] left-[-10px] block max-w-none"
                                        style={{ imageRendering: 'pixelated' }}
                                    />
                                </Link>

                                <div className="min-w-0">
                                    <div className="text-coal_dark font-bold text-[20px] leading-tight uppercase">
                                        {article.title}
                                    </div>

                                    <div className="text-sm text-gray-500 tracking-wide flex items-center gap-2 mt-1 flex-wrap">
                                        <span>{formatDate(article.publicDate)}</span>
                                        <span>•</span>
                                        <span>{article.categoryName}</span>
                                        <span>•</span>
                                        <span>By: <Link to={`/members/profile/${encodeURIComponent(article.author)}`} className="font-bold hover:underline">{article.author}</Link></span>
                                        {article.coAuthors && article.coAuthors.length > 0 && (
                                            <>
                                                <span>•</span>
                                                <span>Co-authors: {article.coAuthors.map((coAuthor, i) => (
                                                    <React.Fragment key={coAuthor}>
                                                        {i > 0 && ', '}
                                                        <Link to={`/members/profile/${encodeURIComponent(coAuthor)}`} className="font-bold hover:underline">{coAuthor}</Link>
                                                    </React.Fragment>
                                                ))}</span>
                                            </>
                                        )}
                                        {article.source && (
                                            <>
                                                <span>•</span>
                                                <span>Source:&nbsp;
                                                    <span className="font-bold">
                                                        <a href={article.source} target="_blank" rel="noopener noreferrer">
                                                            {getPageTitle(article.source)} ⧉
                                                        </a>
                                                    </span>
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {article.headerImage && (
                                <div className="w-full bg-gray-200 border border-gray-300 overflow-y-auto overflow-x-hidden flex items-center justify-center p-[20px] pb-[40px] relative">
                                    <img src={article.headerImage} alt={article.title} />

                                    {article.source && (
                                        <span className="absolute bottom-0 right-2 bg-gray-300 py-[6px] px-[10px] text-xs cursor-pointer">
                                            <a href={article.source} target="_blank" rel="noopener noreferrer">
                                                Source: {getPageTitle(article.source)} ⧉
                                            </a>
                                        </span>
                                    )}
                                </div>
                            )}

                            <div className="article-content space-y-3 text-sm leading-relaxed text-gray-800" dangerouslySetInnerHTML={{ __html: article.articleText }} />

                            <div className="space-y-4">
                                {article.notes && article.notes.length > 0 ? (
                                    article.notes.map((note) => (
                                        <ArticleNote
                                            key={note.id}
                                            type={note.type || (note.important ? 'important' : 'note')}
                                            meta={`${new Date(note.timestamp).toLocaleDateString('en-GB')} • ${new Date(note.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} • Added by ${note.author}`}
                                            xUrl={note.type === 'xtra' ? (note.imageUrl || null) : null}
                                            imageSrc={note.type !== 'xtra' ? (note.imageUrl || null) : null}
                                            imageAlt="Note image"
                                        >
                                            {note.text && note.text.trim() ? note.text : null}
                                        </ArticleNote>
                                    ))
                                ) : null}
                            </div>
                        </div>
                    </ContentBox>

                    <ContentBox theme="default" header="Comments">
                        <ArticleComments articleId={article.id} userData={userData} />
                    </ContentBox>
                </div>

                <div className="lg:col-span-4 min-w-0">
                    {userData && userData.hasStaffRole && (
                        <>
                            <ContentBox theme="default" header="Staff Actions">
                                <StaffArticleActions
                                    articleId={article.id}
                                    userData={userData}
                                    onNoteAdded={loadArticle}
                                />
                            </ContentBox>
                        </>
                    )}

                    <ContentBox theme="default" header="Latest Articles" padding="p-0">
                        <LatestArticles />
                    </ContentBox>

                    <ContentBox theme="poll">
                        <PollBox userData={userData} />
                    </ContentBox>

                    <ContentBox theme="outlines" tint="#45ad00" header="Habbo Profile Search">
                        <ProfileSearch />
                    </ContentBox>
                </div>
            </div>
        </div>
    );
}

export default Article;